import React, { useState } from 'react';
import { Grid3X3, Heart, SquarePlus } from 'lucide-react';
import GradientButtonTab from '@/components/ui/GradientButtonTab';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import GlassPill from '@/components/ui/GlassPill';
import { useIsMobile } from '@/hooks/layout/use-mobile';

interface AppLibraryHeaderProps {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  isLoading?: boolean;
  onRequestApp?: (category?: string) => void;
}

export const AppLibraryHeader: React.FC<AppLibraryHeaderProps> = ({
  selectedCategory,
  onCategoryChange,
  isLoading = false,
  onRequestApp
}) => {
  const { isMobile } = useIsMobile();
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);

  const handleFilterChange = (category: string) => {
    onCategoryChange(category);
  };

  const handleMobileTooltip = (tooltipId: string) => {
    if (isMobile) {
      setOpenTooltip(tooltipId);
      setTimeout(() => setOpenTooltip(null), 1000); // Show for 1 second
    }
  };

  const handleRequestApp = () => {
    if (onRequestApp) {
      const categoryToPass = selectedCategory !== 'all' && selectedCategory !== 'favorites' ? selectedCategory : undefined;
      onRequestApp(categoryToPass);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <div className="flex space-x-2 flex-1 md:flex-initial">
          <TooltipProvider delayDuration={isMobile ? 0 : 300}>
            <Tooltip open={isMobile ? openTooltip === 'all' : undefined}>
              <TooltipTrigger asChild>
                <GradientButtonTab
                  variant="blue"
                  isActive={selectedCategory === 'all'}
                  onClick={() => {
                    handleFilterChange('all');
                    handleMobileTooltip('all');
                  }}
                  icon={<Grid3X3 className="h-4 w-4" />}
                  testId="all-apps-button"
                  fullWidth={true}
                  className="md:w-auto"
                >
                  All Apps
                </GradientButtonTab>
              </TooltipTrigger>
              <TooltipContent className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border border-white/20 shadow-lg">
                View All Apps
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider delayDuration={isMobile ? 0 : 300}>
            <Tooltip open={isMobile ? openTooltip === 'favorites' : undefined}>
              <TooltipTrigger asChild>
                <GradientButtonTab
                  variant="red"
                  isActive={selectedCategory === 'favorites'}
                  onClick={() => {
                    handleFilterChange('favorites');
                    handleMobileTooltip('favorites');
                  }}
                  icon={<Heart className="h-4 w-4" />}
                  testId="favorites-button"
                  fullWidth={true}
                  className="md:w-auto"
                >
                  Favorites
                </GradientButtonTab>
              </TooltipTrigger>
              <TooltipContent className="bg-gradient-to-br from-red-500 to-red-600 text-white border border-white/20 shadow-lg">
                View Your Favorites
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {/* Header/Loading indicator */}
        <div 
          className="hidden md:flex items-center" 
          data-test-id="library-header-indicator"
        >
          <div className={cn(isLoading && "animate-pulse")}>
            <GlassPill
              type={isLoading 
                ? "Loading..." 
                : selectedCategory === 'favorites'
                  ? "Your Favorites"
                  : selectedCategory === 'all'
                    ? "All Apps"
                    : "Category Apps"}
              variant="blue"
              className="text-sm font-medium bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent border-blue-200/50 dark:border-blue-800/30"
            />
          </div>
        </div>
      </div>
    </>
  );
}; 